/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {
	AuditBy,
	DB_Object,
	StringMap
} from "@intuitionrobotics/ts-common";
import type {ComputerVisionAnnotation} from "./computer-vision-tags.js";
import type {DeploymentContext} from "./facts.js";
import type {ApiWithBody} from "@intuitionrobotics/thunderstorm";
import type { InnerNotification } from "./notification.js";

export type Notification = {
	title?: string;
	body?: string;
	imageUrl?: string;
}

export type BucketPointerData = {
	bucketName: string
	path: string
};

export type BucketPointer = {
	bucketPointer: BucketPointerData
	cacheId?: string
	signedUrl?: string
}

export type MediaInfo = OptionalOptions & BucketData & {
	tags?: ComputerVisionAnnotation[]
	landmarks?: ComputerVisionAnnotation[]
	captions?: ComputerVisionAnnotation[]
	// This will be the transcript for audio and the text if there is for images and videos
	text?: string // Reserved for audio transcription
	attachedText?: string
}

export type BucketData = BucketPointer & {
	mimeType: string
	ttl?: number
}

export type OptionalOptions = {
	choices?: MessageOptions
};

export type TextMessage = OptionalOptions & { text: string }

export type MessageMap = {
	audio: MediaInfo
	video: MediaInfo & VideoInfo
	image: MediaInfo
	text: Omit<MediaInfo, "text" | "mimeType" | "attachedText" | "bucketPointer"> &
		{ text: string, mimeType?: string, bucketPointer?: BucketPointerData }
}

export type VideoInfo = {
	thumbnailUrl?: string
	thumbnailData?: BucketData
}

export type MediaTypes = "audio" | "video" | "image"

export type BaseBody = {
	senderId?: string,
	ghostSenderId?: string,
	ghostUserId?: string,
	// _id of the original message
	replyId?: string,
	choiceId?: string
	contactIds?: string[],
	chatIds?: string[]
	deploymentContext?: DeploymentContext
	scheduledSend?: number
};

export type MessageOptions = {
	options: DB_Message<"text">[]
	choiceId?: string
}

export type Body_SendMessageApp = { chatIds: string[], contactIds?: string[], messageIds: string[], senderId: string, deploymentContext?: DeploymentContext };
export type Body_SendTextApp = Body_SendMessageApp & { text: string }
export type Body_SendMediaApp = Body_SendMessageApp & { base64: string, senderId: string, mimeType: string, attachedText?: string }
export type Body_SendMedia =
	BaseBody
	& { filePrefix?: number[], mimeType?: string, attachedText?: string, thumbnailData?: { mimeType: string, base64: string } }
export type Body_ByProxy_SendMedia = BaseBody & { mediaInfo: MediaInfo & VideoInfo }
export type Body_SendExistMedia = BaseBody & { mediaInfo: MediaInfo & VideoInfo }
export type Body_SendText = BaseBody & TextMessage & {
	addToChat?: boolean
	forceSMS?: boolean
	textToApp?: string
	notification?:Notification,
	notificationLocales?: { [key: string]: InnerNotification }, // Map of language initials to localized notification content
}

export type Body_BodyHoroscopePrediction = BaseBody & {
	image: MessageMap["image"],
	audio: MessageMap["audio"],
	notificationLocales: { [key: string]: InnerNotification }, // Map of language initials to localized notification content
}

export type Body_ShoppingList = {
	phoneNumber: string,
	finalUrl: string,
	textMessage: string
	tinyUrlId: string
}

export type Body_SendTwilioText = { text: string, phoneNumber: string }

export type DB_Chat = DB_Object & {
	contactIds: string[]
	deletedContactIds?: string[]
	_updated: AuditBy
	name?: string
	// lastMessage?: string
}

// Only insert and read, no update or delete
export type DB_Message<T extends keyof MessageMap> = DB_Object & {
	chatId: string
	type: T
	data: MessageMap[T]
	senderId: string
	// we need the metadata of the actual user who sent a message
	// (or any other metadata the apps will use)
	// {
	//   ghostSenderId: string
	//   ghostUserId: string
	// }
	metadata?: StringMap
	replyId?: string
	draft: boolean
	timestamp: number
	needsFollowup?: boolean
	// only update cause
	softDelete?: AuditBy
	deploymentContext?: DeploymentContext
	additionalMedia?: AdditionalMedia[]
	locale?: string
}

export type AdditionalMedia = { type: keyof MessageMap, data: MessageMap["text"] | MessageMap["image"] | MessageMap["video"] | MessageMap["audio"] }

export type TextMessagePushPayload = DB_Object & {
	chatId: string
}

export enum MessageStatuses {
	// will need!
	SENT = "SENT",
	RECEIVED = "RECEIVED",
	READ = "READ",
	FAILED = "FAILED",
	RETRIED = "RETRIED",
	QUEUED = "QUEUED",
	SENDING = "SENDING" // Just for react native app UI - Not stored in DB
}

// Only insert and read, no update or delete
export type DB_MessageStatus = DB_Object & {
	messageId: string
	chatId: string
	recipientId: string
	status: MessageStatuses
	timestamp: number
	messageTimestamp: number
	debugMessage?: string,
	lockTrial?: number
}

export type SoftDeleteContactsChats_Body = { contactId: string, agentUserId: string };
export type QueryChatsByContactIds_Body = { contactIds: string[] };
export type Api_ContactsChats_SoftDelete = ApiWithBody<"/v1/chats/soft-delete-chats", SoftDeleteContactsChats_Body, void>
export type Api_QueryChatsByContactIds = ApiWithBody<"/v1/chats/query-by-contact-ids", QueryChatsByContactIds_Body, {chat: DB_Chat | undefined}>
